import { computed, type ComputedRef, type Ref } from 'vue'
import { useHandleEnquiryResponse } from '@/composables/enquiry/useHandleEnquiryResponse'
import type {
    CustomerRegistrationInput,
    RingBuilderResolveExistingCustomerMutation,
    RingBuilderResolveMode,
    RingBuilderResolveNewCustomerMutation,
    RingSizeInput,
} from '@/graphql/api'
import {} from '@/graphql/api'
import { createResponseAndPath, type ResponseAndPath } from '@/helpers/errors'
import { useTranslator } from '@/i18n'
import { useEngagementRingGetInput } from './useEngagementRingGetInput'

import { useRegionPlugin } from '@/plugins/region'
import { useIsAdmin } from '@/components/app/isAdmin'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { useRingBuilderResolveExistingCustomerMutation } from '@/graphql/api/mutations/ringBuilderResolveExistingCustomer.mutation.generated.ts'
import { useRingBuilderResolveNewCustomerMutation } from '@/graphql/api/mutations/ringBuilderResolveNewCustomer.mutation.generated.ts'
import { useErrorBar } from '@/components/app/error-bar/useErrorBar'

export function useEngagementRingCreateEnquiry(
    centreStoneUri: Readonly<Ref<string | null>>,
    engraving: Readonly<Ref<string>>,
    customerRegistrationInput: Readonly<
        Ref<CustomerRegistrationInput | undefined>
    >,
    handleRingSizeNotSelected: () => RingSizeInput | null
): {
    createEnquiryExistingCustomer: (
        enquiryType?: RingBuilderResolveMode
    ) => void
    createEnquiryNewCustomer: (
        successCallback: () => void,
        enquiryType?: RingBuilderResolveMode
    ) => void
    createEnquiryCustomizeFurtherExistingCustomer: () => void
    createEnquiryCustomizeFurtherNewCustomer: (
        successCallback: () => void
    ) => void
    fetchingCreateEnquiry: ComputedRef<boolean>
    createEnquiryErrors: readonly [
        ResponseAndPath<RingBuilderResolveExistingCustomerMutation>,
        ResponseAndPath<RingBuilderResolveNewCustomerMutation>,
    ]
} {
    const { getInput } = useEngagementRingGetInput(
        centreStoneUri,
        engraving,
        handleRingSizeNotSelected
    )
    const currency = useCurrencyInput()
    const { regionCode } = useRegionPlugin()
    const { handleEnquiryResponse } = useHandleEnquiryResponse()
    const isAdmin = useIsAdmin()
    // Existing customer
    const responseExistingCustomer =
        useRingBuilderResolveExistingCustomerMutation()

    const {
        executeMutation: executeExistingCustomerMutation,
        fetching: fetchingExistingCustomer,
    } = responseExistingCustomer

    const { displayError } = useErrorBar()

    const createEnquiryExistingCustomer = (
        enquiryType: RingBuilderResolveMode = 'ENQUIRY'
    ) => {
        const input = getInput(enquiryType)

        if (!input) {
            return
        }

        executeExistingCustomerMutation({
            input,
            region: regionCode.value,
            currency: currency.value,
            locale: locale.value,
        }).then(({ data, error }) => {
            if (!data) {
                return
            }

            switch (data.ringBuilderResolveExistingCustomer.__typename) {
                case 'RingBuilderResolvedExistingCustomerSuccess':
                    handleEnquiryResponse({
                        data: {
                            customer: undefined,
                        },
                        error,
                        redirect:
                            data.ringBuilderResolveExistingCustomer.redirect,
                    })

                    break

                case 'RingBuilderResolvedFailure':
                    if (data.ringBuilderResolveExistingCustomer.errors[0]) {
                        displayError(
                            data.ringBuilderResolveExistingCustomer.errors[0]
                        )
                    }
            }
        })
    }

    const createEnquiryCustomizeFurtherExistingCustomer = () =>
        createEnquiryExistingCustomer('CUSTOMIZE')

    // New customer
    const responseNewCustomer = useRingBuilderResolveNewCustomerMutation()

    const {
        executeMutation: executeNewCustomerMutation,
        fetching: fetchingNewCustomer,
    } = responseNewCustomer

    const { locale } = useTranslator()

    const createEnquiryNewCustomer = (
        successCallback: () => void,
        enquiryType: RingBuilderResolveMode = 'ENQUIRY'
    ) => {
        const input = getInput(enquiryType)
        const customer = customerRegistrationInput.value

        if (!input || !customer) {
            return
        }

        executeNewCustomerMutation({
            input,
            customer,
            region: regionCode.value,
            currency: currency.value,
            locale: locale.value,
            isConsultant: isAdmin.value,
        }).then(({ data, error }) => {
            if (!data) {
                return
            }

            switch (data.ringBuilderResolveNewCustomer.__typename) {
                case 'RingBuilderResolvedNewCustomerSuccess':
                    handleEnquiryResponse({
                        data: data.ringBuilderResolveNewCustomer,
                        error,
                        redirect: data.ringBuilderResolveNewCustomer.redirect,
                    })

                    if (data.ringBuilderResolveNewCustomer.customer.login) {
                        successCallback()
                    }

                    break

                case 'RingBuilderResolvedFailure':
                    if (data.ringBuilderResolveNewCustomer.errors[0]) {
                        displayError(
                            data.ringBuilderResolveNewCustomer.errors[0]
                        )
                    }
            }
        })
    }

    const createEnquiryCustomizeFurtherNewCustomer = (
        successCallback: () => void
    ) => createEnquiryNewCustomer(successCallback, 'CUSTOMIZE')

    const createEnquiryErrors = [
        createResponseAndPath(
            responseExistingCustomer,
            'ringBuilderResolveExistingCustomer'
        ),
        createResponseAndPath(
            responseNewCustomer,
            'ringBuilderResolveNewCustomer'
        ),
    ] as const

    const fetchingCreateEnquiry = computed(
        () => fetchingExistingCustomer.value || fetchingNewCustomer.value
    )

    return {
        createEnquiryExistingCustomer,
        createEnquiryNewCustomer,
        createEnquiryCustomizeFurtherExistingCustomer,
        createEnquiryCustomizeFurtherNewCustomer,
        fetchingCreateEnquiry,
        createEnquiryErrors,
    }
}
